import React from 'react';
import PieChart from '../pie-chart';
import styles from './style.module.scss';

import type { PropsWithTestId } from 'types';

//#region Types
type DataPoint = { value: string; percentage: number };

type ChartWithLegendProps = PropsWithTestId<{
  title: string;
  data: DataPoint[];
}>;
//#endregion

//#region Sub Components

type LegendProps = PropsWithTestId<{
  dataPoint: DataPoint;
  index: number;
}>;

/**
 * Renders a legend for a data point.
 *
 * @param dataPoint The data point to display in the legend.
 * @param index The index of the data point.
 * @param testId The test ID of the component.
 */
function Legend({ dataPoint, index, testId }: LegendProps) {
  return (
    <div className={styles.legend} data-testid={`${testId}-legend-${index}`}>
      <span className={styles['legend-color-value']}>
        <span className={`${styles['legend-color']} ${styles[`legend-color-${index}`]}`}/>
        {dataPoint.value}
      </span>
      <span className={styles.value}>
        {dataPoint.percentage}%
      </span>
    </div>
  );
}
//#endregion

//#region Component

/**
 * Renders a pie chart with a legend.
 *
 * @param title The title of the chart.
 * @param data The data points to display in the chart.
 * @param testId The test ID of the component.
 *
 * Note: Each data point will have a corresponding color in the pie chart up to 7 colors.
 *
 * @example TypeScript
 * ```tsx
 * const data = [
 *   { value: '0-14', percentage: 20 },
 *   { value: '15-24', percentage: 15 },
 *   { value: '25-54', percentage: 40 },
 *   { value: '55+', percentage: 25 },
 * ];
 * return (
 *   <ChartWithLegend title="Population by Age" data={data} testId="population-by-age" />
 * );
 * ```
 */
export default function ChartWithLegend({ title, data, testId }: ChartWithLegendProps) {
  return (
    <div data-testid={testId}>
      <div className={styles.title}>{title}</div>
      <div className={styles['chart-with-legend']}>
        <div className={styles['legend-container']}>
          {data.map((dataPoint, index) => {
            return (
              <Legend dataPoint={dataPoint} index={index} key={index} testId={testId} />
            );
          })}
        </div>
        <PieChart data={data} testId={`${testId}-pie-chart`}/>
      </div>
    </div>
  );
}
//#endregion